import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Printer, Cloud, Server, Database, ShieldCheck, Zap, MessageCircle } from "lucide-react";

const TALKING_POINTS = [
  {
    Icon: MessageCircle,
    label: "The short version",
    text: "Your customer data stays with you. We ask your DMS to send us one small report every night — just the customers who declined a repair. That's it. No login, no plug-in, no software install on your end.",
  },
  {
    Icon: Cloud,
    label: "How the data gets to us",
    text: "Most dealerships already work with a data-delivery company (DealerVault, Fortellis, or IntegraLink) that handles nightly reports for dozens of other services you use. We piggyback on the same trusted pipe. Every night, yesterday's declined services show up in our system.",
  },
  {
    Icon: ShieldCheck,
    label: "What you're asked to sign",
    text: "A one-page Data Consent form from your data-delivery company. It simply says 'yes, please forward my nightly Declined Services report to Decline Drive.' Five minutes, once. That's the entire setup on your end.",
  },
  {
    Icon: Zap,
    label: "What happens next",
    text: "About a week later, your customers who declined a repair start receiving polite discount texts. Every one includes a link to book back into your service drive. You never touch a report, never upload a spreadsheet, never call your DMS rep.",
  },
];

const OBJECTIONS = [
  {
    q: "Is our customer data secure?",
    a: "Yes. Your data travels through the same middleware layer that Xtime, VinSolutions, Kimoby, and every other major automotive vendor uses. It's SOC 2 audited, moves over encrypted TLS 1.2+ connections, and we only ever see declined-service records — never financials, payroll, F&I, or customer social security numbers.",
  },
  {
    q: "What does this cost us?",
    a: "Nothing extra on the data side. The data-delivery fee is bundled into your monthly Decline Drive subscription. You will not see a separate line item on your bill for it.",
  },
  {
    q: "We use CDK / Reynolds — will this work?",
    a: "Yes. CDK and Reynolds are the two largest DMS platforms in the country, and both are fully supported through our data-delivery partners. Every text-follow-up vendor you have ever spoken to uses this same connection.",
  },
  {
    q: "What if our IT department blocks the connection?",
    a: "Rare, but if it happens we fall back to a simple daily upload — your office manager saves the Declined Services report she already runs and drops it into our app. Takes 30 seconds. Every other feature still works.",
  },
  {
    q: "Can we start today?",
    a: "Yes. We begin with the manual daily upload while the cloud connection spins up in the background (typically 5–10 business days). Your customers start receiving texts by tomorrow morning either way.",
  },
];

const AGGREGATORS = [
  { name: "DealerVault", parent: "a division of Authenticom, Inc.", covers: "CDK · Reynolds · Auto/Mate · Dealertrack · most legacy DMS" },
  { name: "Fortellis", parent: "CDK Global marketplace", covers: "CDK-hosted stores + growing multi-DMS support" },
  { name: "IntegraLink", parent: "CDX Automotive", covers: "CDK · Reynolds certified extractor" },
];

export default function CloudsPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  return (
    <DashboardShell>
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-red-600">🔒 Internal Sales Cheat Sheet — Do NOT hand to dealers</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Cloud Integration — Plain English
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            The pitch you give dealers when they ask &quot;how does this get my customer data?&quot; — written
            in plain, non-technical language you can memorize or read straight off the iPad. This page
            contains internal pricing math you should NOT show a dealer principal. Print for your own
            reference only.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-clouds-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      <Card className="p-4 mb-6 border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-900 no-print" data-testid="clouds-internal-warning">
        <div className="text-sm text-red-900 dark:text-red-200">
          <b>⚠️ Confidential — Internal Use Only.</b> This document contains your take-home
          margin, aggregator pricing, and pass-through cost math. Never leave it on a dealer&apos;s
          desk or share it as a PDF. Use it to prep for the pitch, not during it.
        </div>
      </Card>

      {/* Big one-liner */}
      <Card className="p-6 border-border mb-6 bg-navy text-white" data-testid="clouds-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[hsl(var(--gold))] grid place-items-center shrink-0">
            <Cloud className="w-6 h-6 text-neutral-900" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono-label text-[hsl(var(--gold-light))]">The 1-sentence pitch</div>
            <div className="font-display text-2xl font-bold tracking-tight mt-2 leading-snug">
              &quot;Your DMS already knows every declined service. We pay a data company to hand it to us
              every night. You sign one form and never touch it again.&quot;
            </div>
          </div>
        </div>
      </Card>

      {/* 4 talking points */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {TALKING_POINTS.map(({ Icon, label, text }) => (
          <Card key={label} className="p-5 border-border" data-testid={`clouds-point-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-md bg-navy text-white grid place-items-center shrink-0">
                <Icon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="font-mono-label text-muted-foreground">{label}</div>
            </div>
            <p className="text-sm leading-relaxed text-foreground/90">{text}</p>
          </Card>
        ))}
      </div>

      {/* Data flow diagram — text-based */}
      <Card className="p-6 border-border mb-6" data-testid="clouds-flow">
        <div className="font-mono-label text-muted-foreground mb-1">The Pipeline</div>
        <div className="font-display text-xl font-bold tracking-tight mb-4">
          Where the data comes from, in 4 hops
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <div className="rounded-md border border-border p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-stone-200 text-stone-700 grid place-items-center mx-auto mb-2">
              <Database className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-sm">1. Dealership DMS</div>
            <div className="text-xs text-muted-foreground mt-1">
              CDK, Reynolds, Tekion, etc. — advisors write ROs all day, some services get declined.
            </div>
          </div>
          <div className="rounded-md border border-border p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))] grid place-items-center mx-auto mb-2">
              <Server className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-sm">2. DealerVault / Fortellis</div>
            <div className="text-xs text-muted-foreground mt-1">
              Cloud aggregator extracts declined-service records at midnight.
            </div>
          </div>
          <div className="rounded-md border border-border p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-navy/10 text-navy dark:text-blue-300 grid place-items-center mx-auto mb-2">
              <Cloud className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-sm">3. Decline Drive</div>
            <div className="text-xs text-muted-foreground mt-1">
              File lands in our SFTP box nightly. Auto-ingested, matched to advisors, deduped.
            </div>
          </div>
          <div className="rounded-md border border-border p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-2">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-sm">4. Customer&apos;s phone</div>
            <div className="text-xs text-muted-foreground mt-1">
              Day {s?.stage_1_days ?? 7} → {s?.stage_1_discount ?? 30}% text. Day {s?.stage_2_days ?? 15} → {s?.stage_2_discount ?? 40}% text. Both include a coupon + schedule link.
            </div>
          </div>
        </div>
      </Card>

      {/* Aggregators list */}
      <Card className="p-6 border-border mb-6">
        <div className="font-mono-label text-muted-foreground mb-1">The Companies You&apos;ll Mention</div>
        <div className="font-display text-xl font-bold tracking-tight mb-4">
          Certified DMS aggregators we work with
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {AGGREGATORS.map((a) => (
            <div key={a.name} className="rounded-md border border-border p-4">
              <div className="font-display font-black text-lg">{a.name}</div>
              <div className="text-xs text-muted-foreground italic mb-2">{a.parent}</div>
              <Badge variant="outline" className="mb-2 text-[0.6rem]">COVERS</Badge>
              <div className="text-xs text-foreground/80">{a.covers}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-4 italic">
          Tip: If the dealer asks &quot;who&apos;s DealerVault?&quot; — reply: &quot;They&apos;re the same
          company Xtime uses. If you trust Xtime, you can trust them.&quot;
        </div>
      </Card>

      {/* Objections */}
      <Card className="p-6 border-border mb-6" data-testid="clouds-objections">
        <div className="font-mono-label text-muted-foreground mb-1">Handle Objections</div>
        <div className="font-display text-xl font-bold tracking-tight mb-4">
          What they&apos;ll ask — and exactly what to say
        </div>
        <div className="space-y-4">
          {OBJECTIONS.map((o, i) => (
            <div key={i} className="border-l-4 border-[hsl(var(--gold))] pl-4">
              <div className="font-display font-bold text-sm text-navy dark:text-blue-200">{o.q}</div>
              <div className="text-sm text-foreground/85 mt-1 leading-relaxed">{o.a}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* The close */}
      <Card className="p-6 border-border bg-[hsl(var(--gold))]/10 border-[hsl(var(--gold))] mb-6" data-testid="clouds-close">
        <div className="font-mono-label text-muted-foreground mb-1">The Close</div>
        <div className="font-display text-xl font-bold tracking-tight mb-3">
          Wrap it up like this
        </div>
        <p className="text-sm text-foreground/90 italic leading-relaxed">
          &quot;Here&apos;s what we do next: I email you a one-page data consent form today. You sign it and
          send it back. Within about a week, DealerVault starts sending us your declined services every night
          overnight. Meanwhile, while that&apos;s spinning up, your office manager drops the report into our app
          manually so we don&apos;t lose a day of revenue. You&apos;re making money by next Monday. Sound
          fair?&quot;
        </p>
      </Card>

      {/* Cost Structure — the hard numbers (INTERNAL — hidden from print/handouts) */}
      <Card className="p-6 border-border mb-6 no-print" data-testid="clouds-cost-structure">
        <div className="font-mono-label text-muted-foreground mb-1">Cost Structure</div>
        <div className="font-display text-xl font-bold tracking-tight mb-2">
          What the DMS feed actually costs
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl mb-5">
          If a dealer pushes back on price, walk them through the real market rates for a DMS data feed.
          Every aggregator charges a monthly per-rooftop fee — we get wholesale pricing you don&apos;t.
        </p>

        {/* Market rates table */}
        <div className="rounded-md border border-border overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="text-left px-4 py-2 font-mono-label text-muted-foreground">Provider</th>
                <th className="text-left px-4 py-2 font-mono-label text-muted-foreground">Cost / rooftop / month</th>
                <th className="text-left px-4 py-2 font-mono-label text-muted-foreground">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-semibold">DealerVault (Authenticom)</td>
                <td className="px-4 py-2 tabular-nums font-bold text-[hsl(var(--gold))]">$50 – $150</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">Most common · multi-DMS · pay per feed</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-semibold">Fortellis (CDK)</td>
                <td className="px-4 py-2 tabular-nums font-bold">$100 – $200</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">Best for CDK-hosted stores</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-semibold">IntegraLink (CDX)</td>
                <td className="px-4 py-2 tabular-nums font-bold">$75 – $150</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">CDK + Reynolds specialist</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-semibold">Direct DMS API (Tekion, Dominion, PBS)</td>
                <td className="px-4 py-2 tabular-nums font-bold text-emerald-600">$0 – $50</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">Sometimes free with cloud DMS</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Two pricing strategies */}
        <div className="font-display font-bold text-base mb-3">Two ways to price it to the dealer</div>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Option A */}
          <div className="border-2 border-[hsl(var(--gold))] rounded-md p-4 bg-[hsl(var(--gold))]/5">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-[hsl(var(--gold))] text-neutral-900">OPTION A · RECOMMENDED</Badge>
            </div>
            <div className="font-display font-black text-base mb-1">Bundle it — flat $1,599</div>
            <p className="text-xs text-muted-foreground mb-3">
              Bake the aggregator fee into your subscription. Dealer sees ONE bill.
            </p>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Dealer pays</span><span className="tabular-nums font-semibold">$1,599</span></div>
              <div className="flex justify-between text-muted-foreground"><span>− DealerVault feed</span><span className="tabular-nums">$100</span></div>
              <div className="flex justify-between text-muted-foreground"><span>− Twilio (~1k texts)</span><span className="tabular-nums">$10</span></div>
              <div className="flex justify-between text-muted-foreground"><span>− Hosting + infra</span><span className="tabular-nums">$30</span></div>
              <div className="flex justify-between pt-2 border-t border-border font-display font-bold text-emerald-700">
                <span>Your net</span><span className="tabular-nums">~$1,459/mo</span>
              </div>
            </div>
            <div className="mt-3 text-xs italic text-foreground/80 border-l-2 border-[hsl(var(--gold))] pl-2">
              &quot;Everything&apos;s included — the data feed, the texting, the dashboard, unlimited advisors,
              unlimited coupons. One flat monthly.&quot;
            </div>
          </div>

          {/* Option B */}
          <div className="border border-border rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">OPTION B · PASS-THROUGH</Badge>
            </div>
            <div className="font-display font-black text-base mb-1">Itemize it — $1,599 + $150</div>
            <p className="text-xs text-muted-foreground mb-3">
              Show the aggregator as a separate line item. Total bill is higher but transparency wins some dealers.
            </p>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Platform subscription</span><span className="tabular-nums font-semibold">$1,599</span></div>
              <div className="flex justify-between"><span>+ DealerVault feed (pass-through)</span><span className="tabular-nums font-semibold">$150</span></div>
              <div className="flex justify-between pt-2 border-t border-border font-display font-bold">
                <span>Dealer pays</span><span className="tabular-nums">$1,749/mo</span>
              </div>
              <div className="flex justify-between text-muted-foreground text-xs mt-2">
                <span>(Same net margin either way)</span>
              </div>
            </div>
            <div className="mt-3 text-xs italic text-foreground/80 border-l-2 border-border pl-2">
              &quot;$1,599 for the platform + $150 pass-through for DealerVault. Same $150 you&apos;d pay if you
              tried to hire someone yourself — we get the wholesale rate.&quot;
            </div>
          </div>
        </div>

        {/* Annual math box */}
        <div className="mt-6 p-5 rounded-md bg-navy text-white">
          <div className="font-mono-label text-[hsl(var(--gold-light))] mb-2">Your Take-Home Math (Option A)</div>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="font-display text-3xl font-black text-[hsl(var(--gold))] tabular-nums">$17,500</div>
              <div className="text-xs mt-1 text-white/70">Net per rooftop / year</div>
            </div>
            <div>
              <div className="font-display text-3xl font-black text-[hsl(var(--gold))] tabular-nums">+$999</div>
              <div className="text-xs mt-1 text-white/70">One-time onboarding (nearly all margin)</div>
            </div>
            <div>
              <div className="font-display text-3xl font-black text-[hsl(var(--gold))] tabular-nums">~$350k</div>
              <div className="text-xs mt-1 text-white/70">Annual recurring at 20 rooftops</div>
            </div>
          </div>
          <div className="text-xs text-white/60 text-center mt-3">
            Before adding recovery fee % on top of subscription. Achievable in year 1 in a mid-size market.
          </div>
        </div>
      </Card>

      <div className="text-xs text-muted-foreground border-t border-border pt-4 max-w-3xl mt-6">
        © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. Print this and keep it in
        your demo folder — you&apos;ll close more dealers when you can talk cloud integration in plain English.
      </div>
    </DashboardShell>
  );
}
